import React, { useState, useCallback, useEffect } from 'react';
import GuestRequestButtons from './GuestRequestButtons';
import DoNotDisturbToggle from './DoNotDisturbToggle';
import { db } from '../firebase';
import { useTranslation } from 'react-i18next';
import {
    collection,
    query,
    where,
    getDocs,
    updateDoc,
    doc,
    addDoc,
    deleteDoc,
    onSnapshot,
    orderBy, 
} from 'firebase/firestore';
import Chat from './Chat';
import StayRequestButton from './StayRequestButton'; 
import GuestFeedbackForm from './GuestFeedbackForm'; 

// === HELPER: Copy to Clipboard ===
const copyToClipboard = (text, successCallback) => {
    // ... (rest of the function)
    navigator.clipboard.writeText(text).then(() => {
        if(successCallback) successCallback();
    }).catch(err => {
        console.error('Could not copy text: ', err);
    });
};

export default function GuestStatus() {
    const { t } = useTranslation();
    const [key, setKey] = useState('');
    const [guestData, setGuestData] = useState(null);
    const [chatId, setChatId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [notifications, setNotifications] = useState([]);
    const [hasUnread, setHasUnread] = useState(false);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [isChatMaximized, setIsChatMaximized] = useState(false);
    const [wifiSettings, setWifiSettings] = useState({ ssid: 'N/A', password: 'N/A' });

    const closeChat = useCallback(() => {
        setIsChatOpen(false);
        setIsChatMaximized(false);
    }, []);

    const toggleMaximize = useCallback(() => {
        setIsChatMaximized(prev => !prev);
    }, []);

    const handleChatOpened = useCallback(async (currentChatId) => {
        if (!currentChatId) return;
        try {
            await updateDoc(doc(db, 'chats', currentChatId), {
                guestHasUnread: false,
            });
            console.log("Unread chat flag cleared in DB (chats document).");
        } catch (error) {
            console.error("Failed to clear unread chat flag:", error);
        }
    }, []);

    // === NOTIFICATION LISTENER (THE CORE ISSUE) ===
    useEffect(() => {
        if (!guestData?.id) return;
        
        console.warn(`[NOTIFS LISTENER] 🚨 Querying for Guest ID: ${guestData.id}`);
        console.warn(`[NOTIFS LISTENER] 🚨 Querying for isread: false`);
        
        const notificationsQuery = query(
            collection(db, 'notifications'),
            where('guestId', '==', guestData.id),
            // THIS MUST BE EXACTLY WHAT THE STAFF DASHBOARD WRITES
            where('isread', '==', false), 
            orderBy('timestamp', 'desc')
        );

        const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
            
            console.log(`[NOTIFS] Listener Triggered. 🟢 Unread docs found: ${snapshot.size}`); 
            
            if (snapshot.size === 0) {
                console.error("[NOTIFS] No documents found. Run the diagnostic test below.");
            }

            const currentUnread = snapshot.docs
                .map(d => {
                    const data = d.data();
                    return {
                        id: d.id,
                        ...data,
                        timestamp: data.timestamp?.toDate()?.toLocaleTimeString() ?? 'N/A'
                    };
                });
            
            snapshot.docChanges().forEach(change => {
                if (change.type === "added") {
                    const notification = change.doc.data();
                    alert(`🔔 STAFF NOTIFICATION: ${notification.message}`);
                }
            });
            
            setNotifications(currentUnread);
        }, (error) => {
            console.error("🔥 Error fetching guest notifications (Index/Rules issue):", error);
        });
        
        return () => unsubscribe();
    }, [guestData]);
    // === END NOTIFICATION LISTENER ===

    // ... (rest of the component logic: Chat Listener, Handlers, Render)
    
    // The rest of the code is unchanged from the previous, correct version.

// ... (remaining functions and return statement)
    // Chat Document Listener (to track unread chat status) - Keep this
    useEffect(() => {
        if (!chatId) return;
        const chatDocRef = doc(db, 'chats', chatId);
        const unsubscribe = onSnapshot(chatDocRef, (docSnapshot) => {
            if (docSnapshot.exists()) {
                const data = docSnapshot.data();
                setHasUnread(data.guestHasUnread === true);
            }
        }, (error) => {
            console.error("Error fetching real-time chat data:", error);
        });
        return () => unsubscribe();
    }, [chatId]);

    // === HANDLER: ACKNOWLEDGE NOTIFICATION (DELETES IT) ===
    const handleNotificationClick = async (notificationId) => {
        try {
            // Delete the document to mark it as read and remove it from the listener
            await deleteDoc(doc(db, 'notifications', notificationId));
            console.log(`Notification ${notificationId} deleted instantly (Marked as read).`);
        } catch (error) {
            console.error("Failed to delete notification. Check Firebase Rules:", error);
        }
    };

    // === HANDLER: ACCESS KEY SUBMISSION (Authentication Logic) ===
    const handleAccess = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        const cleanKey = key.trim().toUpperCase();

        if (cleanKey.length < 6) {
            return setError(t('guest_dash.err_key_length'));
        }
        setLoading(true);
        try {
            const guestQuery = query(
                collection(db, 'guests'),
                where('accessKey', '==', cleanKey),
                where('status', '!=', 'CHECKED_OUT_FINALIZED')
            );

            const guestSnap = await getDocs(guestQuery);
            if (guestSnap.empty) {
                setError(t('guest_dash.err_invalid_key'));
                setGuestData(null);
                setChatId(null);
                setLoading(false);
                return;
            }
            
            const guestDocData = guestSnap.docs[0].data();
            const currentGuest = {
                id: guestSnap.docs[0].id,
                ...guestDocData,
                roomId: guestDocData.roomId
            };

            // Fetch Hotel Settings for Wi-Fi (Non-blocking)
            const settingsQuery = query(collection(db, 'hotelSettings'), where('hotelId', '==', currentGuest.hotelId));
            const settingsDocs = await getDocs(settingsQuery);

            if (!settingsDocs.empty) {
                const settings = settingsDocs.docs[0].data();
                setWifiSettings({
                    ssid: settings.wifiSSID || 'Not Set',
                    password: settings.wifiPassword || 'Not Set'
                });
            }
            let foundChatId = null;

            // Find Associated Chat Room
            const chatQuery = query(
                collection(db, 'chats'),
                where('guestId', '==', currentGuest.id),
                where('hotelId', '==', currentGuest.hotelId)
            );
            const chatSnap = await getDocs(chatQuery);

            if (!chatSnap.empty) {
                foundChatId = chatSnap.docs[0].id;
            } else {
                console.log("No chat found, creating new one...");
                const newChatRef = await addDoc(collection(db, 'chats'), {
                    hotelId: currentGuest.hotelId,
                    guestId: currentGuest.id,
                    guestName: currentGuest.fullName,
                    lastMessageTimestamp: null,
                    staffHasUnread: false,
                    guestHasUnread: false,
                    roomNumber: currentGuest.roomNumber
                });
                foundChatId = newChatRef.id;
            }

            setGuestData(currentGuest);
            setChatId(foundChatId);
        } catch (err) {
            console.error("Access Error (Firebase or Query Failure):", err);
            setError(t('checkout.err_firebase'));
            setGuestData(null);
            setChatId(null);
        } finally {
            setLoading(false);
        }
    };

    const handleWifiCopy = (text) => {
        copyToClipboard(text, () => {
            setSuccess(t('guest_dash.wifi_copied', { text }));
            setTimeout(() => setSuccess(''), 3000);
        });
    };

    const handleCheckoutRequest = async () => {
        if (!guestData || guestData.status !== 'CHECKED_IN') return;
        if (!window.confirm(t('guest_dash.confirm_checkout'))) return;
        try {
            await updateDoc(doc(db, 'guests', guestData.id), {
                status: 'CHECKOUT_REQUESTED',
            });
            setGuestData(prev => ({ ...prev, status: 'CHECKOUT_REQUESTED' }));
            setSuccess(t('checkout.success_request'));
        } catch (err) {
            setError(t('checkout.err_firebase'));
        }
    };

    const formatStatus = (status) => status ? status.replace(/_/g, ' ').toUpperCase() : 'N/A';
    
    // Only allow dashboard access for active statuses
    const isCheckedIn = guestData?.status === 'CHECKED_IN' || guestData?.status === 'CHECKOUT_REQUESTED' || guestData?.status === 'AWAITING_GUEST_FEEDBACK';
    
    return (
        <div className="container" style={{paddingTop: '50px'}}>
            <h2>{t('guest_dash.title')}</h2>
            {error && <p className="error-message info-box" style={{color: 'red', border: '1px solid red', padding: '10px'}}>{error}</p>}
            {success && <p className="success-message info-box" style={{color: 'green', border: '1px solid green', padding: '10px'}}>{success}</p>}

            {/* 🔥 NOTIFICATION ALERTS */}
            {notifications.map(notification => (
                <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification.id)}
                    style={{
                        padding: '15px',
                        backgroundColor: '#fffae0',
                        borderLeft: '5px solid #ffc107',
                        borderRadius: '8px',
                        marginBottom: '15px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                    title={t('guest_dash.dismiss_hint')}
                >
                    🔔 {notification.message}
                    <span style={{float: 'right', fontWeight: 'normal', fontSize: '0.9em', color: '#555'}}>
                        ({notification.timestamp})
                    </span>
                </div>
            ))}
            {/* END NOTIFICATION ALERTS */}
            
            {loading && (
                <div className="card" style={{maxWidth: '400px', margin: '0 auto'}}>
                    <p style={{textAlign: 'center', margin: 0}}>{t('guest_dash.loading')}</p>
                </div>
            )}
            {!guestData && !loading && (
                <div className="card" style={{maxWidth: '400px', margin: '0 auto', padding: '20px', border: '1px solid #ccc', borderRadius: '8px'}}>
                    <h3 style={{marginTop: 0}}>{t('guest_dash.enter_key')}</h3>
                    <form onSubmit={handleAccess}>
                        <input
                            type="text"
                            value={key}
                            onChange={(e) => setKey(e.target.value)}
                            placeholder="e.g., OP2NRN"
                            maxLength="8"
                            style={{textAlign: 'center', fontSize: '1.5em', letterSpacing: '3px', marginBottom: '15px', width: '100%', padding: '10px', boxSizing: 'border-box'}}
                            required
                        />
                        <button type="submit" disabled={loading} style={{width: '100%', padding: '10px', backgroundColor: 'var(--color-primary, #1e90ff)', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer'}}>
                            {t('guest_dash.btn_view')}
                        </button>
                    </form>
                </div>
            )}
            
            {/* ------------------------------------------- */}
            {/* IN-STAY DASHBOARD (Visible when guest is active) */}
            {/* ------------------------------------------- */}
            {guestData && isCheckedIn && (
                <>
                    <div className="card" style={{padding: '20px', backgroundColor: '#e9f7ff', borderLeft: '5px solid var(--color-primary)', borderRadius: '8px'}}>
                        <h3 style={{margin: 0}}>{t('guest_dash.welcome', { name: guestData.fullName.split(' ')[0] })}</h3>
                        <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '10px', fontSize: '1.1em'}}>
                            <p>{t('checker.room_prefix')}:**{guestData.roomNumber}**</p>
                            <p>{t('guest_dash.status')}: **{formatStatus(guestData.status)}**</p>
                        </div>
                        <p style={{marginBottom: 0}}>{t('registration.section1_checkout')}: {
                            guestData.checkOutDate
                                ? new Date(guestData.checkOutDate.toDate ? guestData.checkOutDate.toDate() : guestData.checkOutDate).toLocaleDateString()
                                : 'N/A'
                        }</p>
                    </div>

                    <div className="section-title" style={{marginTop: '40px', fontWeight: 'bold', fontSize: '1.2em'}}>🛎️ {t('guest_dash.actions_title')}</div>

                    <DoNotDisturbToggle
                        guestId={guestData.id}
                        roomId={guestData.roomId}
                    />

                    <GuestRequestButtons
                        guestId={guestData.id}
                        roomId={guestData.roomId}
                        hotelId={guestData.hotelId}
                        roomNumber={guestData.roomNumber}
                    />

                    <StayRequestButton
                        bookingKey={guestData.id} 
                        roomNumber={guestData.roomNumber} 
                        hotelId={guestData.hotelId}
                        currentCheckInDate={guestData.checkInDate} 
                        currentCheckOutDate={guestData.checkOutDate}
                    />

                    <div style={{display: 'flex', gap: '15px', marginBottom: '40px', flexWrap: 'wrap'}}>
                        <div className="card" style={{flex: '1 1 300px', padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '8px'}}>
                            <strong>{t('guest_dash.wifi_title')}</strong>
                            <p style={{margin: '5px 0'}}>SSID: {wifiSettings.ssid}</p>
                            <p style={{margin: '5px 0'}}>{t('registration.card_details_cvv')}: {wifiSettings.password}</p>
                            <button onClick={() => handleWifiCopy(wifiSettings.ssid)} style={{marginRight: '10px', padding: '8px 12px', borderRadius: '5px'}}>Copy SSID</button>
                            <button onClick={() => handleWifiCopy(wifiSettings.password)} style={{padding: '8px 12px', borderRadius: '5px'}}>Copy Password</button>
                        </div>
                        <div className="card" style={{flex: '1 1 300px', padding: '15px', backgroundColor: '#f9f9f9', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', borderRadius: '8px'}}>
                            <strong>{t('guest_dash.end_stay_title')}</strong>
                            <p>{t('guest_dash.end_stay_desc')}</p>
                            <button
                                onClick={handleCheckoutRequest}
                                disabled={guestData.status === 'CHECKOUT_REQUESTED'}
                                style={{
                                    backgroundColor: guestData.status === 'CHECKOUT_REQUESTED' ? '#aaa' : 'red',
                                    color: 'white',
                                    padding: '10px',
                                    border: 'none',
                                    borderRadius: '5px',
                                    cursor: 'pointer'
                                }}
                            >
                                {guestData.status === 'CHECKOUT_REQUESTED' ? t('guest_dash.btn_checkout_sent') : t('checkout.btn_submit')}
                            </button>
                        </div>
                    </div>

                    {/* ------------------------------------------- */}
                    {/* ✅ FEEDBACK FORM SECTION */}
                    {/* ------------------------------------------- */}
                    <div style={{ marginTop: '40px', marginBottom: '40px' }}>
                        <h3 style={{textAlign: 'center', color: '#007bff'}}>{t('guest_dash.feedback_title')}</h3>
                        <p style={{textAlign: 'center'}}>{t('guest_dash.feedback_desc')}</p>
                        <GuestFeedbackForm bookingKey={guestData.id} /> 
                    </div>

                    {/* Floating Chat Button (Visible when chat is closed) */}
                    {!isChatOpen && (
                        <button
                            className="floating-chat-btn"
                            onClick={() => {
                                setIsChatOpen(true);
                                handleChatOpened(chatId);
                            }}
                            title={t('guest_dash.open_chat')}
                            style={{
                                position: 'fixed',
                                bottom: '20px',
                                right: '20px',
                                width: '60px',
                                height: '60px',
                                borderRadius: '50%',
                                backgroundColor: 'var(--color-primary, #1e90ff)',
                                color: 'white',
                                border: 'none',
                                fontSize: '1.5em',
                                cursor: 'pointer',
                                boxShadow: '0 3px 6px rgba(0,0,0,0.3)',
                                zIndex: 1002
                            }}
                        >
                            💬
                            {/* 🔥 NEW BADGE DISPLAY 🔥 */}
                            {hasUnread && (
                                <span style={{
                                    position: 'absolute', top: '5px', right: '5px',
                                    backgroundColor: 'red', color: 'white',
                                    borderRadius: '50%', width: '18px', height: '18px',
                                    fontSize: '11px', lineHeight: '18px', textAlign: 'center',
                                    fontWeight: 'bold', border: '2px solid white'
                                }}>
                                    !
                                </span>
                            )}
                        </button>
                    )}
                    {/* Chat Display (Conditional Rendering) */}
                    {isChatOpen && chatId && (
                        <div
                            style={{
                                position: 'fixed',
                                bottom: isChatMaximized ? '0' : '75px',
                                right: isChatMaximized ? '0' : '20px',
                                width: isChatMaximized ? '100vw' : '350px',
                                height: isChatMaximized ? '100vh' : '500px',
                                maxWidth: '100vw',
                                maxHeight: '100vh',
                                display: 'flex',
                                flexDirection: 'column',
                                zIndex: 1000,
                                backgroundColor: 'white',
                                boxShadow: isChatMaximized ? 'none' : '0 4px 12px rgba(0,0,0,0.2)',
                                borderRadius: isChatMaximized ? '0' : '10px',
                                overflow: 'hidden',
                                transition: 'all 0.3s ease'
                            }}
                        >
                            {/* === CHAT HEADER WITH CONTROLS (Hide/Maximize) === */}
                            <div
                                style={{
                                    backgroundColor: 'var(--color-primary, #1e90ff)',
                                    color: 'white',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '8px 10px',
                                    flexShrink: 0,
                                    borderTopLeftRadius: isChatMaximized ? '0' : '10px',
                                    borderTopRightRadius: isChatMaximized ? '0' : '10px',
                                }}
                            >
                                <strong style={{marginLeft: '5px'}}>{t('guest_dash.chat_header')}</strong>
                                <div>
                                    <button
                                        onClick={toggleMaximize}
                                        title={isChatMaximized ? t('guest_dash.minimize') : t('guest_dash.maximize')}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: 'white',
                                            cursor: 'pointer',
                                            fontSize: '1.5em',
                                            padding: '5px 10px',
                                            marginRight: '10px'
                                        }}
                                    >
                                        {isChatMaximized ? '⤢' : '⤡'}
                                    </button>
                                    <button
                                        onClick={closeChat}
                                        title={t('guest_dash.hide_chat')}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: 'white',
                                            cursor: 'pointer',
                                            fontSize: '2em',
                                            fontWeight: 'lighter',
                                            padding: '5px 10px'
                                        }}
                                    >
                                        ×
                                    </button>
                                </div>
                            </div>
                            {/* === END CHAT HEADER === */}
                            <Chat
                                currentChatId={chatId}
                                isStaff={false}
                                isGuestEmbedded={true}
                                hotelId={guestData.hotelId}
                                guestId={guestData.id}
                                guestData={guestData}
                                closeChat={closeChat}
                                toggleMaximize={toggleMaximize}
                                isChatMaximized={isChatMaximized}
                                style={{ flexGrow: 1 }}
                            />
                        </div>
                    )}
                </>
            )}

            {/* CHECKED OUT FINALIZED message */}
            {guestData?.status === 'CHECKED_OUT_FINALIZED' && (
                <div className="card" style={{maxWidth: '400px', margin: '0 auto', padding: '30px', textAlign: 'center'}}>
                    <h3 style={{color: '#28a745'}}>{t('guest_dash.stay_complete_title')} 👋</h3>
                    <p>{t('guest_dash.stay_complete_desc')}</p>
                </div>
            )}
            
        </div>
    );
}